import { Link } from 'react-router-dom';
import ModernNavigation from '@/components/ModernNavigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Bike, Route, Camera, UtensilsCrossed, Waves } from 'lucide-react';

interface VillagePageProps {
  villageName: string;
  description: string;
  attractions: string[];
  bikeRoutes: string[];
  practicalInfo: {
    beaches?: string[];
    restaurants?: string[];
    markets?: string[];
  };
}

const VillagePage = ({ 
  villageName, 
  description, 
  attractions,
  bikeRoutes,
  practicalInfo 
}: VillagePageProps) => {
  return (
    <div className="min-h-screen bg-background">
      <ModernNavigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 to-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link 
              to="/destinations/ile-de-re" 
              className="inline-flex items-center text-emerald-600 hover:text-emerald-700 text-sm font-medium mb-4"
            >
              ← Retour à l'Île de Ré
            </Link>
          </div>

          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <MapPin className="w-4 h-4 mr-2" />
              Île de Ré
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Location de vélos à {villageName}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {description}
            </p>
          </div>

          {/* Attractions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <Camera className="w-6 h-6 text-emerald-600 mr-3" />
                  <h2 className="text-xl font-bold text-gray-900">À découvrir</h2>
                </div>
                <ul className="space-y-2">
                  {attractions.map((attraction, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      <span className="text-gray-700">{attraction}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <Route className="w-6 h-6 text-emerald-600 mr-3" />
                  <h2 className="text-xl font-bold text-gray-900">Balades à vélo</h2>
                </div>
                <ul className="space-y-2">
                  {bikeRoutes.map((route, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      <span className="text-gray-700">{route}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Practical Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {practicalInfo.beaches && practicalInfo.beaches.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center mb-3">
                    <Waves className="w-5 h-5 text-emerald-600 mr-2" />
                    <h3 className="font-semibold text-gray-900">Plages</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {practicalInfo.beaches.map((beach, index) => (
                      <li key={index}>• {beach}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {practicalInfo.restaurants && practicalInfo.restaurants.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center mb-3">
                    <UtensilsCrossed className="w-5 h-5 text-emerald-600 mr-2" />
                    <h3 className="font-semibold text-gray-900">Restaurants</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {practicalInfo.restaurants.map((restaurant, index) => (
                      <li key={index}>• {restaurant}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {practicalInfo.markets && practicalInfo.markets.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center mb-3">
                    <MapPin className="w-5 h-5 text-emerald-600 mr-2" />
                    <h3 className="font-semibold text-gray-900">Marchés</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {practicalInfo.markets.map((market, index) => (
                      <li key={index}>• {market}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* CTA Section */}
          <div className="text-center bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Prêt à explorer {villageName} à vélo ?
            </h2>
            <p className="text-gray-600 mb-6">
              Réservez votre vélo dès maintenant et profitez de votre séjour
            </p>
            <Link to="/reserver">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all">
                <Bike className="w-5 h-5 mr-2" />
                Louer un vélo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VillagePage;